import * as React from 'react';
import {
  WIDGET_REGISTRY,
  WidgetInstance,
  ChartType,
} from './charts/widgetRegistry.ts';
import {
  Box, CircularProgress, Typography,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { DESC_MAP } from '../utils/utils.ts';


type WidgetDialogProps = {
  open:    boolean;
  initial: Partial<WidgetInstance>;
  osmName: string;
  onClose: () => void;
  onSave:  (w: Partial<WidgetInstance>) => Promise<void>;
};

export function WidgetDialog({ open, initial, osmName, onClose, onSave }: WidgetDialogProps) {
  const [form, setForm]   = React.useState<Partial<WidgetInstance>>(initial);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => setForm(initial), [initial]);

  const set = (field: keyof WidgetInstance, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setConfig = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, config: { ...(prev.config as any ?? {}), [key]: value } }));

  const widgetDef = WIDGET_REGISTRY.find((w) => w.id === form.widget_type_id);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({ ...form, osm_name: osmName });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>{form.id ? 'Edit Widget' : 'Add Widget'}</DialogTitle>
    <DialogContent sx={{ overflowY: 'auto', maxHeight: '70vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

          <TextField
            select
            label="Widget Type"
            value={form.widget_type_id ?? 'sensor_gauge'}
            onChange={(e) => {
              const def = WIDGET_REGISTRY.find((w) => w.id === e.target.value);
              set('widget_type_id', e.target.value);
              set('chart_type', def?.chartTypes[0] ?? 'gauge');
              set('config', null);
            }}
          >
            {WIDGET_REGISTRY.map((w) => (
              <MenuItem key={w.id} value={w.id}>
                <Box>
                  <Typography variant="body1">{w.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{w.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {widgetDef && (
            <TextField
              select
              label="Chart Type"
              value={form.chart_type ?? widgetDef.chartTypes[0]}
              onChange={(e) => set('chart_type', e.target.value as ChartType)}
            >
              {widgetDef.chartTypes.map((ct) => (
                <MenuItem key={ct} value={ct}>
                  {ct.charAt(0).toUpperCase() + ct.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          )}

          <TextField
            label="Title (optional)"
            value={form.title ?? ''}
            onChange={(e) => set('title', e.target.value || null)}
          />

          <TextField
            label="Colour"
            type="color"
            value={form.colour ?? '#1976d2'}
            onChange={(e) => set('colour', e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 80 }}
          />

          {widgetDef?.configFields.map((field) => {
            const value = (form.config as any)?.[field.key] ?? field.default ?? '';

            if (field.type === 'measurement') {
              return (
                <Box key={field.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {field.label}{field.required ? ' *' : ''}
                  </Typography>
                  <select
                    value={value}
                    required={field.required}
                    onChange={(e) => setConfig(field.key, e.target.value)}
                    style={{
                      padding:         '12px',
                      borderRadius:    '4px',
                      border:          '1px solid rgba(255,255,255,0.23)',
                      backgroundColor: 'transparent',
                      color:           'inherit',
                      fontSize:        '16px',
                      width:           '100%',
                      cursor:          'pointer',
                    }}
                  >
                    <option value="" disabled>Select measurement</option>
                    {Object.entries(DESC_MAP).map(([k, label]) => (
                      <option key={k} value={k}>{label}</option>
                    ))}
                  </select>
                </Box>
              );
            }

            if (field.type === 'select') {
              return (
                <TextField
                  key={field.key}
                  select
                  label={field.label}
                  value={value}
                  onChange={(e) => setConfig(field.key, e.target.value)}
                  required={field.required}
                >
                  {field.options?.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              );
            }

            return (
              <TextField
                key={field.key}
                label={field.label}
                type={field.type === 'number' ? 'number' : 'text'}
                value={value}
                onChange={(e) =>
                  setConfig(field.key, field.type === 'number' ? Number(e.target.value) : e.target.value)
                }
                required={field.required}
              />
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || !form.widget_type_id}
          startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
